import RAPIER from '@dimforge/rapier3d-compat';
import type { Collider, RigidBody, World } from '@dimforge/rapier3d-compat';
import * as THREE from 'three';

import type { MazeLayout } from './mazeLayout';
import { buildFallbackMazeGroup } from './MazeVisuals';
import { buildMazeColliders } from '../physics/colliders/mazeColliders';
import { createKinematicBody } from '../physics/bodies';
import { radToDeg, wrapAngle } from '../utils/math';

/**
 * The rotating maze: a kinematic rigid body, its colliders, and the matching
 * visual group.
 *
 * ---------------------------------------------------------------------------
 * WHY `kinematicPositionBased` + `setNextKinematicRotation`
 * ---------------------------------------------------------------------------
 * Rapier derives a kinematic POSITION-based body's effective velocity from the
 * delta between its current transform and the `next*` target it has been given.
 * That derived velocity is what the solver uses when applying friction, so a
 * ball resting on the board is genuinely DRAGGED by the rotating walls instead
 * of being clipped through them.
 *
 * The alternatives do not work as well here:
 *  - A `fixed` body whose colliders are repositioned by hand conveys no velocity
 *    at all, so the ball would never be carried along.
 *  - `kinematicVelocityBased` + `setAngvel` does work, but hands the angle over
 *    to Rapier's integrator and makes the unbounded accumulator harder to own.
 *
 * ---------------------------------------------------------------------------
 * UNBOUNDED ANGLE
 * ---------------------------------------------------------------------------
 * `angleRad` accumulates forever and is never clamped or normalised, so the
 * player can keep spinning the board past 360 degrees as many times as they
 * like - which is exactly how the physical machine behaves, and what the
 * gyroscope control model in Phase 4 needs.
 *
 * The wrap to [0, 2*PI) exists ONLY because a quaternion is periodic: it keeps
 * sin/cos away from very large arguments, where precision would degrade over a
 * long session. The HUD always shows the raw unbounded value.
 */
export class MazeAssembly {
  /** Root transform the scene holds. Copies the kinematic body every step. */
  readonly group: THREE.Group;
  /**
   * The board's visual: the loaded `maze_assembly.glb`, or the procedural
   * fallback. Exposed so the integration tests can measure it.
   */
  readonly board: THREE.Object3D;
  readonly body: RigidBody;
  readonly colliders: Collider[];
  /** True when the production model could not be used. */
  readonly usedFallback: boolean;

  /** Unbounded rotation accumulator, in radians. */
  private angleRad = 0;

  constructor(world: World, layout: MazeLayout, board?: THREE.Object3D) {
    this.usedFallback = !board;

    // The group carries the body's transform; the board hangs off it. Wrapping
    // rather than using the model as the root means the fallback and the loaded
    // model are driven identically, and neither has to have an identity
    // transform of its own.
    this.group = new THREE.Group();
    this.group.name = 'maze-assembly';
    this.board = board ?? buildFallbackMazeGroup(layout);
    this.group.add(this.board);

    this.body = createKinematicBody(world, 0, 0, 0);
    this.colliders = buildMazeColliders(world, this.body, layout);
  }

  /** Unbounded board rotation in degrees. May exceed +/-360. */
  get angleDeg(): number {
    return radToDeg(this.angleRad);
  }

  get angleRadians(): number {
    return this.angleRad;
  }

  /**
   * Advance the board by exactly one FIXED physics step.
   *
   * Must be called exactly once per `world.step()`. Calling it per rendered
   * frame instead would corrupt the derived kinematic velocity on displays
   * faster than 60 Hz.
   */
  step(omega: number, dt: number): void {
    this.angleRad += omega * dt;

    const half = wrapAngle(this.angleRad) / 2;
    // Rotation about Z only, so x and y stay zero.
    this.body.setNextKinematicRotation(
      new RAPIER.Quaternion(0, 0, Math.sin(half), Math.cos(half)),
    );
  }

  /** Copy the simulated transform onto the visual group. */
  syncVisuals(): void {
    const rotation = this.body.rotation();
    this.group.quaternion.set(rotation.x, rotation.y, rotation.z, rotation.w);

    const translation = this.body.translation();
    this.group.position.set(translation.x, translation.y, translation.z);
  }

  /**
   * Jump the board to an absolute angle.
   *
   * The board is kinematic, so moving it is a matter of writing the target the
   * solver will integrate from. `setRotation` moves it immediately as well, so
   * the colliders are in their new place before the next step and the derived
   * kinematic velocity is zero - the board teleports rather than sweeping.
   *
   * This is how the tests line a specific ring gap up with the top of the board
   * without having to guess how far a given `omega` will overshoot.
   */
  setAngle(angleRad: number): void {
    this.angleRad = angleRad;

    const half = wrapAngle(angleRad) / 2;
    const rotation = new RAPIER.Quaternion(0, 0, Math.sin(half), Math.cos(half));
    this.body.setNextKinematicRotation(rotation);
    this.body.setRotation(rotation, true);
    this.syncVisuals();
  }

  /**
   * Return the board to rotation zero.
   *
   * Called on restart so every round begins from the same orientation. Without
   * this the board would keep whatever angle the previous round ended at, and
   * which ring gaps happen to line up with the bottom - i.e. whether a given
   * descent is even possible - would depend on how the last round was played.
   *
   * The accumulator is unbounded during play; this is a deliberate reset at a
   * round boundary, not a clamp.
   */
  resetAngle(): void {
    this.setAngle(0);
  }
}
